import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("neobank_token");
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function formatError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Une erreur est survenue";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  }
  return JSON.stringify(detail);
}

// Map of well-known French backend error details -> i18n keys. Used by tError().
// Only covers user-facing surfaces (bank app, auth). Extend as needed.
const BACKEND_ERROR_KEYS = {
  "Identifiants invalides": "err.invalid_credentials",
  "Code PIN incorrect": "err.bad_pin",
  "Compte bloqué": "err.account_blocked",
  "Mot de passe actuel incorrect": "err.bad_current_pw",
  "Fonds insuffisants, veuillez créditer votre compte.": "err.insufficient_funds",
  "Montant supérieur à l'épargne disponible": "err.amount_above_savings",
  "La valeur doit être positive": "err.must_be_positive",
  "Format invalide": "err.invalid_format",
  "Email déjà utilisé.": "err.email_taken",
  "Email déjà utilisé pour cette banque": "err.email_taken",
  "Carte introuvable": "err.card_not_found",
  "Carte physique déjà commandée": "err.physical_card_already_ordered",
  "Bénéficiaire introuvable": "err.beneficiary_not_found",
  "Code invalide": "err.invalid_code",
  "Aucun code pour ce virement": "err.no_transfer_code",
  "Lien expiré. Veuillez recommencer.": "err.link_expired",
  "Lien invalide ou expiré": "err.link_expired",
  "Conversation introuvable": "err.thread_not_found",
  "Message vide": "err.empty_message",
  "Fichier trop volumineux (max 20 Mo)": "err.file_too_large",
  "Accès interdit": "err.forbidden",
  "Banque introuvable": "err.bank_not_found",
  "Les inscriptions sont fermées pour cette banque.": "err.signups_closed",
  "Accès temporairement suspendu. Veuillez contacter votre banque.": "err.access_suspended",
  "Cette notification ne peut pas être fermée.": "err.notif_not_closable",
};

// Returns an error string already translated when possible.
// Usage: toast.error(tError(err, t))
export function tError(err, t) {
  const raw = formatError(err);
  if (typeof raw === "string" && BACKEND_ERROR_KEYS[raw]) {
    const translated = t(BACKEND_ERROR_KEYS[raw]);
    // If translation returned the key itself, fall back to raw
    return translated && !translated.startsWith("err.") ? translated : raw;
  }
  return raw;
}

export function fmtMoney(amount, currency = "EUR") {
  const n = Number(amount || 0);
  if (currency?.toUpperCase() === "COIN" || currency?.toUpperCase() === "COINS") {
    return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n) + " Coins";
  }
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n) + " " + (currency || "EUR");
  }
}

// ── Pays (Europe + Amérique) avec drapeau / indicatif / langue / devise ─────
// Each entry: { name, flag, phone, lang, currency }
export const COUNTRIES = [
  // ─── Europe ───
  { name: "Albanie",           flag: "🇦🇱", phone: "+355",  lang: "sq",  currency: "ALL" },
  { name: "Allemagne",         flag: "🇩🇪", phone: "+49",   lang: "de",  currency: "EUR" },
  { name: "Andorre",           flag: "🇦🇩", phone: "+376",  lang: "ca",  currency: "EUR" },
  { name: "Autriche",          flag: "🇦🇹", phone: "+43",   lang: "de",  currency: "EUR" },
  { name: "Belgique",          flag: "🇧🇪", phone: "+32",   lang: "fr",  currency: "EUR" },
  { name: "Biélorussie",       flag: "🇧🇾", phone: "+375",  lang: "be",  currency: "RUB" },
  { name: "Bosnie-Herzégovine",flag: "🇧🇦", phone: "+387",  lang: "bs",  currency: "EUR" },
  { name: "Bulgarie",          flag: "🇧🇬", phone: "+359",  lang: "bg",  currency: "BGN" },
  { name: "Chypre",            flag: "🇨🇾", phone: "+357",  lang: "el",  currency: "EUR" },
  { name: "Croatie",           flag: "🇭🇷", phone: "+385",  lang: "hr",  currency: "EUR" },
  { name: "Danemark",          flag: "🇩🇰", phone: "+45",   lang: "da",  currency: "DKK" },
  { name: "Espagne",           flag: "🇪🇸", phone: "+34",   lang: "es",  currency: "EUR" },
  { name: "Estonie",           flag: "🇪🇪", phone: "+372",  lang: "et",  currency: "EUR" },
  { name: "Finlande",          flag: "🇫🇮", phone: "+358",  lang: "fi",  currency: "EUR" },
  { name: "France",            flag: "🇫🇷", phone: "+33",   lang: "fr",  currency: "EUR" },
  { name: "Grèce",             flag: "🇬🇷", phone: "+30",   lang: "el",  currency: "EUR" },
  { name: "Hongrie",           flag: "🇭🇺", phone: "+36",   lang: "hu",  currency: "HUF" },
  { name: "Irlande",           flag: "🇮🇪", phone: "+353",  lang: "en",  currency: "EUR" },
  { name: "Islande",           flag: "🇮🇸", phone: "+354",  lang: "is",  currency: "ISK" },
  { name: "Italie",            flag: "🇮🇹", phone: "+39",   lang: "it",  currency: "EUR" },
  { name: "Kosovo",            flag: "🇽🇰", phone: "+383",  lang: "sq",  currency: "EUR" },
  { name: "Lettonie",          flag: "🇱🇻", phone: "+371",  lang: "lv",  currency: "EUR" },
  { name: "Liechtenstein",     flag: "🇱🇮", phone: "+423",  lang: "de",  currency: "CHF" },
  { name: "Lituanie",          flag: "🇱🇹", phone: "+370",  lang: "lt",  currency: "EUR" },
  { name: "Luxembourg",        flag: "🇱🇺", phone: "+352",  lang: "fr",  currency: "EUR" },
  { name: "Macédoine du Nord", flag: "🇲🇰", phone: "+389",  lang: "mk",  currency: "MKD" },
  { name: "Malte",             flag: "🇲🇹", phone: "+356",  lang: "mt",  currency: "EUR" },
  { name: "Moldavie",          flag: "🇲🇩", phone: "+373",  lang: "ro",  currency: "EUR" },
  { name: "Monaco",            flag: "🇲🇨", phone: "+377",  lang: "fr",  currency: "EUR" },
  { name: "Monténégro",        flag: "🇲🇪", phone: "+382",  lang: "cnr", currency: "EUR" },
  { name: "Norvège",           flag: "🇳🇴", phone: "+47",   lang: "no",  currency: "NOK" },
  { name: "Pays-Bas",          flag: "🇳🇱", phone: "+31",   lang: "nl",  currency: "EUR" },
  { name: "Pologne",           flag: "🇵🇱", phone: "+48",   lang: "pl",  currency: "PLN" },
  { name: "Portugal",          flag: "🇵🇹", phone: "+351",  lang: "pt",  currency: "EUR" },
  { name: "République tchèque",flag: "🇨🇿", phone: "+420",  lang: "cs",  currency: "CZK" },
  { name: "Roumanie",          flag: "🇷🇴", phone: "+40",   lang: "ro",  currency: "RON" },
  { name: "Royaume-Uni",       flag: "🇬🇧", phone: "+44",   lang: "en",  currency: "GBP" },
  { name: "Russie",            flag: "🇷🇺", phone: "+7",    lang: "ru",  currency: "RUB" },
  { name: "Saint-Marin",       flag: "🇸🇲", phone: "+378",  lang: "it",  currency: "EUR" },
  { name: "Serbie",            flag: "🇷🇸", phone: "+381",  lang: "sr",  currency: "RSD" },
  { name: "Slovaquie",         flag: "🇸🇰", phone: "+421",  lang: "sk",  currency: "EUR" },
  { name: "Slovénie",          flag: "🇸🇮", phone: "+386",  lang: "sl",  currency: "EUR" },
  { name: "Suède",             flag: "🇸🇪", phone: "+46",   lang: "sv",  currency: "SEK" },
  { name: "Suisse",            flag: "🇨🇭", phone: "+41",   lang: "de",  currency: "CHF" },
  { name: "Turquie",           flag: "🇹🇷", phone: "+90",   lang: "tr",  currency: "TRY" },
  { name: "Ukraine",           flag: "🇺🇦", phone: "+380",  lang: "uk",  currency: "UAH" },
  { name: "Vatican",           flag: "🇻🇦", phone: "+379",  lang: "it",  currency: "EUR" },
  // ─── Caucase / Asie centrale ───
  { name: "Arménie",           flag: "🇦🇲", phone: "+374",  lang: "ru",  currency: "AMD" },
  { name: "Azerbaïdjan",       flag: "🇦🇿", phone: "+994",  lang: "ru",  currency: "AZN" },
  { name: "Géorgie",           flag: "🇬🇪", phone: "+995",  lang: "ru",  currency: "GEL" },
  { name: "Kazakhstan",        flag: "🇰🇿", phone: "+7",    lang: "ru",  currency: "KZT" },
  // ─── Amérique ───
  { name: "Antigua-et-Barbuda",flag: "🇦🇬", phone: "+1268", lang: "en",  currency: "USD" },
  { name: "Argentine",         flag: "🇦🇷", phone: "+54",   lang: "es",  currency: "ARS" },
  { name: "Bahamas",           flag: "🇧🇸", phone: "+1242", lang: "en",  currency: "USD" },
  { name: "Barbade",           flag: "🇧🇧", phone: "+1246", lang: "en",  currency: "USD" },
  { name: "Belize",            flag: "🇧🇿", phone: "+501",  lang: "en",  currency: "USD" },
  { name: "Bolivie",           flag: "🇧🇴", phone: "+591",  lang: "es",  currency: "BOB" },
  { name: "Brésil",            flag: "🇧🇷", phone: "+55",   lang: "pt",  currency: "BRL" },
  { name: "Canada",            flag: "🇨🇦", phone: "+1",    lang: "en",  currency: "CAD" },
  { name: "Chili",             flag: "🇨🇱", phone: "+56",   lang: "es",  currency: "CLP" },
  { name: "Colombie",          flag: "🇨🇴", phone: "+57",   lang: "es",  currency: "COP" },
  { name: "Costa Rica",        flag: "🇨🇷", phone: "+506",  lang: "es",  currency: "USD" },
  { name: "Cuba",              flag: "🇨🇺", phone: "+53",   lang: "es",  currency: "USD" },
  { name: "Dominique",         flag: "🇩🇲", phone: "+1767", lang: "en",  currency: "USD" },
  { name: "Équateur",          flag: "🇪🇨", phone: "+593",  lang: "es",  currency: "USD" },
  { name: "États-Unis",        flag: "🇺🇸", phone: "+1",    lang: "en",  currency: "USD" },
  { name: "Grenade",           flag: "🇬🇩", phone: "+1473", lang: "en",  currency: "USD" },
  { name: "Guatemala",         flag: "🇬🇹", phone: "+502",  lang: "es",  currency: "USD" },
  { name: "Guyana",            flag: "🇬🇾", phone: "+592",  lang: "en",  currency: "USD" },
  { name: "Haïti",             flag: "🇭🇹", phone: "+509",  lang: "ht",  currency: "USD" },
  { name: "Honduras",          flag: "🇭🇳", phone: "+504",  lang: "es",  currency: "USD" },
  { name: "Jamaïque",          flag: "🇯🇲", phone: "+1876", lang: "en",  currency: "JMD" },
  { name: "Mexique",           flag: "🇲🇽", phone: "+52",   lang: "es",  currency: "MXN" },
  { name: "Nicaragua",         flag: "🇳🇮", phone: "+505",  lang: "es",  currency: "USD" },
  { name: "Panama",            flag: "🇵🇦", phone: "+507",  lang: "es",  currency: "PAB" },
  { name: "Paraguay",          flag: "🇵🇾", phone: "+595",  lang: "es",  currency: "PYG" },
  { name: "Pérou",             flag: "🇵🇪", phone: "+51",   lang: "es",  currency: "PEN" },
  { name: "République dominicaine", flag: "🇩🇴", phone: "+1809", lang: "es",  currency: "DOP" },
  { name: "Saint-Christophe-et-Niévès", flag: "🇰🇳", phone: "+1869", lang: "en", currency: "USD" },
  { name: "Sainte-Lucie",      flag: "🇱🇨", phone: "+1758", lang: "en",  currency: "USD" },
  { name: "Saint-Vincent-et-les-Grenadines", flag: "🇻🇨", phone: "+1784", lang: "en", currency: "USD" },
  { name: "Salvador",          flag: "🇸🇻", phone: "+503",  lang: "es",  currency: "USD" },
  { name: "Suriname",          flag: "🇸🇷", phone: "+597",  lang: "nl",  currency: "USD" },
  { name: "Trinité-et-Tobago", flag: "🇹🇹", phone: "+1868", lang: "en",  currency: "USD" },
  { name: "Uruguay",           flag: "🇺🇾", phone: "+598",  lang: "es",  currency: "UYU" },
  { name: "Venezuela",         flag: "🇻🇪", phone: "+58",   lang: "es",  currency: "VES" },
];

// ── Langues (Europe + Amérique) ────────────────────────────────────────────
export const LANGUAGES = [
  { value: "fr", label: "Français" },
  { value: "en", label: "Anglais" },
  { value: "es", label: "Espagnol" },
  { value: "pt", label: "Portugais" },
  { value: "de", label: "Allemand" },
  { value: "it", label: "Italien" },
  { value: "nl", label: "Néerlandais" },
  { value: "lb", label: "Luxembourgeois" },
  { value: "sv", label: "Suédois" },
  { value: "no", label: "Norvégien" },
  { value: "da", label: "Danois" },
  { value: "pl", label: "Polonais" },
  { value: "cs", label: "Tchèque" },
  { value: "ro", label: "Roumain" },
  { value: "hu", label: "Hongrois" },
  { value: "tr", label: "Turc" },
  { value: "ca", label: "Catalan" },
  { value: "eu", label: "Basque" },
  { value: "ga", label: "Irlandais" },
  { value: "fi", label: "Finnois" },
  { value: "el", label: "Grec" },
  { value: "sk", label: "Slovaque" },
  { value: "sl", label: "Slovène" },
  { value: "hr", label: "Croate" },
  { value: "sr", label: "Serbe" },
  { value: "bg", label: "Bulgare" },
  { value: "et", label: "Estonien" },
  { value: "lv", label: "Letton" },
  { value: "lt", label: "Lituanien" },
  { value: "uk", label: "Ukrainien" },
  { value: "ru", label: "Russe" },
  { value: "is", label: "Islandais" },
  { value: "mt", label: "Maltais" },
  { value: "sq", label: "Albanais" },
  { value: "mk", label: "Macédonien" },
  { value: "bs", label: "Bosnien" },
  { value: "cnr", label: "Monténégrin" },
  { value: "be", label: "Biélorusse" },
  { value: "qu", label: "Quechua" },
  { value: "gn", label: "Guarani" },
  { value: "ht", label: "Créole haïtien" },
];

// ── Devises (Europe + Amérique) ────────────────────────────────────────────
export const CURRENCIES = [
  { value: "EUR", label: "Euro (€)" },
  { value: "USD", label: "Dollar américain (USD)" },
  { value: "CAD", label: "Dollar canadien (CAD)" },
  { value: "MXN", label: "Peso mexicain (MXN)" },
  { value: "BRL", label: "Réal brésilien (BRL)" },
  { value: "ARS", label: "Peso argentin (ARS)" },
  { value: "COP", label: "Peso colombien (COP)" },
  { value: "CLP", label: "Peso chilien (CLP)" },
  { value: "PEN", label: "Sol péruvien (PEN)" },
  { value: "DOP", label: "Peso dominicain (DOP)" },
  { value: "JMD", label: "Dollar jamaïcain (JMD)" },
  { value: "PAB", label: "Balboa panaméen (PAB)" },
  { value: "UYU", label: "Peso uruguayen (UYU)" },
  { value: "PYG", label: "Guarani paraguayen (PYG)" },
  { value: "BOB", label: "Boliviano (BOB)" },
  { value: "VES", label: "Bolívar vénézuélien (VES)" },
  { value: "CHF", label: "Franc suisse (CHF)" },
  { value: "GBP", label: "Livre sterling (GBP)" },
  { value: "SEK", label: "Couronne suédoise (SEK)" },
  { value: "NOK", label: "Couronne norvégienne (NOK)" },
  { value: "DKK", label: "Couronne danoise (DKK)" },
  { value: "PLN", label: "Złoty (PLN)" },
  { value: "CZK", label: "Couronne tchèque (CZK)" },
  { value: "RON", label: "Leu roumain (RON)" },
  { value: "HUF", label: "Forint hongrois (HUF)" },
  { value: "TRY", label: "Livre turque (TRY)" },
  { value: "RUB", label: "Rouble russe (RUB)" },
  { value: "UAH", label: "Hryvnia ukrainienne (UAH)" },
  { value: "HRK", label: "Kuna croate (HRK)" },
  { value: "BGN", label: "Lev bulgare (BGN)" },
  { value: "RSD", label: "Dinar serbe (RSD)" },
  { value: "ALL", label: "Lek albanais (ALL)" },
  { value: "MKD", label: "Denar macédonien (MKD)" },
  { value: "ISK", label: "Couronne islandaise (ISK)" },
];
